// Static Image Background Swap's form (live's `ImageBackgroundSwapForm`, spec G14-templates):
// the picture whose subject stays and the new background; one tile, live's fixed model.
import { Group, Stack, Text, Textarea } from "@mantine/core";
import { useState } from "react";

import { ImageTemplateQuote, ImageTemplateRequest } from "../../../api/generated/zod";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { usePricedStart } from "../../../platform/jobs/priced-start";
import { GlassPanel } from "../../../ui/GlassPanel";
import {
  exampleImage,
  ImageSlot,
  ReuseSwitch,
  slotSource,
  StartArrow,
  type FormExample,
  type SlotImage,
  type TemplateFormProps,
} from "./kit";
import { CostChip } from "../TemplateCost";

/** The route's bound on the sentence (`BACKGROUND_PROMPT_CHARS`). */
const PROMPT_MAX = 3000;

interface Values {
  image: SlotImage | null;
  prompt: string;
}

/** The form's values: the card's example's (its picture and live's sentence), or empty. */
function valuesOf(example: FormExample | null): Values | null {
  const inputs = example?.inputs;
  if (example === null || inputs?.template !== "image-background-swap") return null;
  return { image: exampleImage(example, inputs.image), prompt: inputs.prompt };
}

/**
 * Live's form: "Reuse example" (on fills the card's example, off clears the form; Replicate opens
 * it on), the "Image" slot (the library with upload, and the products' photos: "Pick the image to
 * place on a new background"), the sentence ("Describe the new background you want."), and the
 * round start, off until both are there. A start leaves the form as it was for another, as live's
 * panel stays.
 */
export function BackgroundSwapForm(props: TemplateFormProps) {
  const { teamId, brandId, above, example, onStarted } = props;
  const [collapsed, setCollapsed] = useState(false);
  const [start] = useState(() => (props.replicate ? valuesOf(example) : null));
  const [reuse, setReuse] = useState(start !== null);
  const [image, setImage] = useState<SlotImage | null>(start?.image ?? null);
  const [prompt, setPrompt] = useState(start?.prompt ?? "");
  const fill = (on: boolean) => {
    const values = on ? valuesOf(example) : null;
    setReuse(values !== null);
    setImage(values?.image ?? null);
    setPrompt(values?.prompt ?? "");
  };
  const inputs =
    image === null
      ? null
      : {
          template: "image-background-swap" as const,
          image: slotSource(image),
          prompt,
        };
  const body = inputs === null ? null : { type: "image.template" as const, inputs };
  const request = ImageTemplateRequest.safeParse(body);
  const quote = ImageTemplateQuote.safeParse(body);
  const job = usePricedStart({
    teamId,
    brandId,
    quote: quote.success ? quote.data : undefined,
    request: request.success ? request.data : undefined,
    onStarted,
  });
  return (
    <GlassPanel
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      above={above}
      header={<ReuseSwitch example={example} checked={reuse} onChange={fill} />}
      summary={<Text size="sm">Image Background Swap</Text>}
      footer={
        <Group justify="flex-end" wrap="nowrap" gap={12}>
          <Group gap={8} wrap="nowrap">
            <CostChip estimate={props.template.estimate} outputs={1} />
            <StartArrow canStart={job.canStart} starting={job.start.isPending} onStart={job.startJob} />
          </Group>
        </Group>
      }
    >
      <Stack gap={12}>
        <ImageSlot
          teamId={teamId}
          brandId={brandId}
          label="Image"
          title="Pick the image to place on a new background"
          value={image}
          onChange={setImage}
        />
        <Textarea
          variant="unstyled"
          aria-label="New background"
          placeholder="Describe the new background you want."
          autosize
          minRows={2}
          maxRows={6}
          maxLength={PROMPT_MAX}
          value={prompt}
          onChange={(event) => setPrompt(event.currentTarget.value)}
        />
        {job.quoteError === null ? null : <ErrorAlert error={job.quoteError} />}
        {job.start.isError ? <ErrorAlert error={job.start.error} /> : null}
        {/* Live's toast, said in the panel (web/AGENTS.md: no toasts). */}
        {job.start.isSuccess ? (
          <Text component="output" size="sm">
            Generation started
          </Text>
        ) : null}
      </Stack>
    </GlassPanel>
  );
}
